/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { dbRealtime, ref, get, update, remove } from '../environments/firebase';
import { jsPDF } from 'jspdf';
import { useAuth } from '../contexts/AuthContext';

type PravachanData = {
  [timestamp: string]: {
    vakta: string;
    topic: string;
    dateTime: string;
    description: string;
    notes: string;
    images?: string[];
    createdAt?: number;
  };
};

// SVG Icons
const EditIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
  </svg>
);

const DeleteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
  </svg>
);

export default function PravachanReport() {
  const [data, setData] = useState<PravachanData>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    vakta: '',
    topic: '',
    description: '',
    notes: ''
  });
  const { currentUser } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) return;
      
      try {
        const snapshot = await get(ref(dbRealtime, `users/${currentUser.uid}/MsmAshirwad`));
        if (snapshot.exists()) {
          setData(snapshot.val());
        } else {
          console.log('No data found');
          setData({});
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  // Convert object to sorted array by date (newest first)
  const sortedPravachans = Object.entries(data)
    .map(([timestamp, entry]) => ({
      timestamp,
      ...entry,
      dateObj: new Date(entry.dateTime || timestamp)
    }))
    .sort((a, b) => b.dateObj.getTime() - a.dateObj.getTime());

  const handleEdit = (pravachan: any) => {
    setEditingId(pravachan.timestamp);
    setEditForm({
      vakta: pravachan.vakta,
      topic: pravachan.topic,
      description: pravachan.description || '',
      notes: pravachan.notes
    });
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const saveEdit = async () => {
    if (!editingId || !currentUser) return;
    
    try {
      await update(
        ref(dbRealtime, `users/${currentUser.uid}/MsmAshirwad/${editingId}`), 
        { 
          vakta: editForm.vakta,
          topic: editForm.topic,
          description: editForm.description,
          notes: editForm.notes
        }
      );
      setEditingId(null);
      // Refresh data
      const snapshot = await get(ref(dbRealtime, `users/${currentUser.uid}/MsmAshirwad`));
      if (snapshot.exists()) {
        setData(snapshot.val());
      }
    } catch (error) {
      console.error('Error updating pravachan:', error);
    }
  };

  const handleDelete = async (timestamp: string) => {
    if (!currentUser || !window.confirm('Are you sure you want to delete this pravachan?')) return;
    
    try {
      await remove(ref(dbRealtime, `users/${currentUser.uid}/MsmAshirwad/${timestamp}`));
      // Update local state
      const newData = { ...data };
      delete newData[timestamp];
      setData(newData);
    } catch (error) {
      console.error('Error deleting pravachan:', error);
    }
  };

  const downloadAllPDF = async () => {
    setIsGeneratingPDF(true);
    
    try {
      // Create a new PDF document
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;

      // Track pages for TOC
      const tocEntries: { topic: string; page: number }[] = [];
      let currentPage = 1;

      // Function to add page numbers
      const addPageNumber = () => {
        pdf.setFontSize(10);
        pdf.setTextColor(100);
        pdf.text(`Page ${currentPage}`, pageWidth - margin, 10);
      };

      // ===== TITLE PAGE =====
      pdf.setFontSize(28);
      pdf.setTextColor(30, 58, 138);
      pdf.text('Pravachan Notes', pageWidth / 2, pageHeight / 2 - 20, { align: 'center' });
      
      pdf.setFontSize(14);
      pdf.setTextColor(100);
      pdf.text('www.swamishreeji.com', pageWidth / 2, pageHeight / 2 + 10, { align: 'center' });
      
      addPageNumber();
      currentPage++;

      // ===== NOTE PAGE =====
      pdf.addPage();
      let yPosition = 30;

      // Heading
      pdf.setFontSize(16);
      pdf.setTextColor(30, 58, 138);
      pdf.text('Jay Swaminarayan!', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;

      // Note content
      pdf.setFontSize(11);
      pdf.setTextColor(0);
      
      const noteLines = [
        "Welcome to SwamiShreeji.com – a platform created with the humble intention of supporting spiritual growth in Satsang.",
        "",
        "All PDFs are generated online and contain content written by the users themselves.",
        "",
        "May this platform serve as a small step toward greater spiritual awareness.",
        "",
        "- Das"
      ];

      noteLines.forEach(line => {
        if (line === "") {
          yPosition += 5;
          return;
        }
        
        const wrappedLines = pdf.splitTextToSize(line, pageWidth - 2 * margin);
        wrappedLines.forEach((wrappedLine: string) => {
          if (yPosition > pageHeight - 15) {
            pdf.addPage();
            currentPage++;
            addPageNumber();
            yPosition = 30;
          }
          pdf.text(wrappedLine, margin, yPosition);
          yPosition += 7;
        });
      });

      addPageNumber();
      currentPage++;

      // ===== TABLE OF CONTENTS =====
      pdf.addPage();
      yPosition = 30;
      addPageNumber();

      pdf.setFontSize(16);
      pdf.setTextColor(30, 58, 138);
      pdf.text('Table of Contents', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 20;

      const tocPage = currentPage;
      currentPage++;

      // ===== PRAVACHAN CONTENT =====
      pdf.addPage();
      yPosition = 30;
      addPageNumber();

      // Function to load image with retries
      const loadImage = async (url: string): Promise<HTMLImageElement> => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'Anonymous';
          
          // Try direct load first
          img.src = url;
          
          img.onload = () => resolve(img);
          img.onerror = () => {
            // Fallback to CORS proxy if direct load fails
            const proxyUrl = `https://cors-anywhere.herokuapp.com/${url}`;
            const proxyImg = new Image();
            proxyImg.crossOrigin = 'Anonymous';
            proxyImg.src = proxyUrl;
            
            proxyImg.onload = () => resolve(proxyImg);
            proxyImg.onerror = () => {
              console.error('Failed to load image:', url);
              // Return a placeholder image
              const blankImg = new Image();
              blankImg.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjYWFhIiBmb250LWZhbWlseT0ic2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCI+SW1hZ2UgTm90IExvYWRlZDwvdGV4dD48L3N2Zz4=';
              blankImg.onload = () => resolve(blankImg);
            };
          };
        });
      };

      // Process each pravachan (oldest first)
      for (const [index, pravachan] of Array.from(sortedPravachans.slice().reverse().entries())) {
        // Check if we need a new page
        if (yPosition > pageHeight - 50) {
          pdf.addPage();
          currentPage++;
          yPosition = 30;
          addPageNumber();
        }

        // Record TOC entry
        tocEntries.push({
          topic: pravachan.topic,
          page: currentPage
        });

        // Add topic (centered)
        pdf.setFontSize(16);
        pdf.setTextColor(30, 58, 138);
        pdf.setFont('helvetica', 'bold');
        pdf.text(pravachan.topic, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 10;

        // Add space between topic and metadata
        yPosition += 8;

        // Add vakta (right-aligned)
        pdf.setFontSize(12);
        pdf.setTextColor(55, 65, 81);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`- ${pravachan.vakta}`, pageWidth - margin, yPosition, { align: 'right' });
        yPosition += 6;

        // Add date (right-aligned, lighter color)
        pdf.setFontSize(10);
        pdf.setTextColor(100);
        const formattedDate = pravachan.dateObj.toLocaleString('en-US', {
          weekday: 'short',
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
        pdf.text(formattedDate, pageWidth - margin, yPosition, { align: 'right' });
        yPosition += 12;

        // Add description if it exists
        if (pravachan.description) {
          pdf.setFontSize(12);
          pdf.setTextColor(100); // Gray color
          pdf.setFont('helvetica', 'italic');
          
          const descriptionLines = pdf.splitTextToSize(`Sabha Description: ${pravachan.description}`, pageWidth - 2 * margin);
          
          descriptionLines.forEach((line: string) => {
            if (yPosition > pageHeight - 15) {
              pdf.addPage();
              currentPage++;
              yPosition = 30;
              addPageNumber();
            }
            pdf.text(line, margin, yPosition);
            yPosition += 7;
          });
          
          // Add some space after description
          yPosition += 10;
        }

        // Add "Guruhari Ashirwad" heading
        pdf.setFontSize(14);
        pdf.setTextColor(30, 58, 138);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Guruhari Ashirwad:', margin, yPosition);
        yPosition += 10;

        // Add images if they exist
        if (pravachan.images && pravachan.images.length > 0) {
          try {
            // Load the first image
            const img = await loadImage(pravachan.images[0]);
            
            // Calculate dimensions to fit half page width while maintaining aspect ratio
            const maxWidth = (pageWidth - 2 * margin) / 2;
            const maxHeight = 80; // Max height for image
            let width = img.width;
            let height = img.height;
            
            if (width > maxWidth) {
              const ratio = maxWidth / width;
              width *= ratio;
              height *= ratio;
            }
            
            if (height > maxHeight) {
              const ratio = maxHeight / height;
              width *= ratio;
              height *= ratio;
            }
            
            // Check if we need a new page
            if (yPosition + height > pageHeight - 15) {
              pdf.addPage();
              currentPage++;
              yPosition = 30;
              addPageNumber();
            }
            
            // Add image to PDF
            pdf.addImage(img, 'JPEG', margin, yPosition, width, height);
            yPosition += height + 10;
          } catch (error) {
            console.error('Error adding image to PDF:', error);
          }
        }

        // Process notes - preserve original formatting exactly
        const notesText = pravachan.notes || 'No notes recorded';
        
        // Split by newlines and preserve all characters
        const notesLines = notesText.split('\n');

        // Add notes text
        pdf.setFontSize(12);
        pdf.setTextColor(0);
        pdf.setFont('helvetica', 'normal');
        
        notesLines.forEach(line => {
          // Skip empty lines (but preserve the space)
          if (line.trim() === '') {
            yPosition += 7;
            return;
          }
          
          // Split long lines to fit page width
          const wrappedLines = pdf.splitTextToSize(line, pageWidth - 2 * margin);
          
          wrappedLines.forEach((wrappedLine: string) => {
            if (yPosition > pageHeight - 15) {
              pdf.addPage();
              currentPage++;
              yPosition = 30;
              addPageNumber();
            }
            pdf.text(wrappedLine, margin, yPosition);
            yPosition += 7; // Line height
          });
        });

        // Add separator if not last item
        if (index < sortedPravachans.length - 1) {
          yPosition += 10;
          pdf.setDrawColor(200);
          pdf.line(margin, yPosition, pageWidth - margin, yPosition);
          yPosition += 15;
        }
      }

      // ===== FILL IN TABLE OF CONTENTS =====
      pdf.setPage(tocPage);
      yPosition = 50;

      pdf.setFontSize(12);
      pdf.setTextColor(0);
      pdf.setFont('helvetica', 'normal');

      tocEntries.forEach(entry => {
        if (yPosition > pageHeight - 20) {
          pdf.addPage();
          currentPage++;
          yPosition = 30;
          addPageNumber();
        }

        pdf.text(entry.topic, margin, yPosition);
        pdf.text(`Page ${entry.page}`, pageWidth - margin, yPosition, { align: 'right' });
        yPosition += 10;

        // Add dotted line between topic and page number
        pdf.setDrawColor(200);
        pdf.setLineWidth(0.2);
        pdf.line(
          margin + pdf.getStringUnitWidth(entry.topic) * pdf.getFontSize() + 2,
          yPosition - 4,
          pageWidth - margin - pdf.getStringUnitWidth(`Page ${entry.page}`) * pdf.getFontSize() - 2,
          yPosition - 4
        );
      });

      // Save the PDF
      pdf.save('katha-amrut-report.pdf');
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-blue-800">Msm Ashirwad Report</h1>
          <button
            onClick={downloadAllPDF}
            disabled={isGeneratingPDF || sortedPravachans.length === 0}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg shadow disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isGeneratingPDF ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Generating...
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Download All as PDF
              </>
            )}
          </button>
        </div>
        
        {sortedPravachans.length === 0 ? (
          <div className="text-center text-gray-600 py-12">
            No Ashirwad notes found. Please add some notes first.
          </div>
        ) : (
          <div className="space-y-6">
            {sortedPravachans.map((pravachan) => (
              <div key={pravachan.timestamp} className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 relative">
                {editingId === pravachan.timestamp ? (
                  <div className="p-6">
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Topic</label>
                      <input
                        type="text"
                        name="topic"
                        value={editForm.topic}
                        onChange={handleEditChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Speaker</label>
                      <input
                        type="text"
                        name="vakta"
                        value={editForm.vakta}
                        onChange={handleEditChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Sabha Description</label>
                      <textarea
                        name="description"
                        value={editForm.description}
                        onChange={handleEditChange}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Describe the sabha atmosphere and details"
                      />
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Guruhari Ashirwad</label>
                      <textarea
                        name="notes"
                        value={editForm.notes}
                        onChange={handleEditChange}
                        rows={6}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => setEditingId(null)}
                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveEdit}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="p-6">
                    <div className="absolute top-4 right-4 flex space-x-2">
                      <button
                        onClick={() => handleEdit(pravachan)}
                        className="p-1 text-blue-600 hover:text-blue-800 rounded-full hover:bg-blue-50"
                        title="Edit"
                      >
                        <EditIcon />
                      </button>
                      <button
                        onClick={() => handleDelete(pravachan.timestamp)}
                        className="p-1 text-red-600 hover:text-red-800 rounded-full hover:bg-red-50"
                        title="Delete"
                      >
                        <DeleteIcon />
                      </button>
                    </div>
                    
                    <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4">
                      <div className="text-sm text-gray-500 mb-2 md:mb-0">
                        {pravachan.dateObj.toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                      <div className="text-xl font-semibold text-center text-blue-700 mb-2 md:mb-0">
                        {pravachan.topic}
                      </div>
                      <div className="text-right font-medium text-purple-700">
                        {pravachan.vakta}
                      </div>
                    </div>

                   
                    <div className="mb-6 border-t border-gray-200 pt-4">
                    {/* Display description if it exists */}
                    {pravachan.description && (
                      <div className="mb-6">
                        <h3 className="text-sm font-medium text-blue-700 mb-2">Sabha's Description:</h3>
                        <div className="whitespace-pre-line text-gray-600 opacity-80">
                          {pravachan.description}
                        </div>
                      </div>
                    )}
                    
                    {/* Display images if they exist */}
                    {pravachan.images && pravachan.images.length > 0 && (
                      <div className="my-4">
                        {pravachan.images.map((imageUrl, idx) => (
                          <img 
                            key={idx}
                            src={imageUrl} 
                            alt={`Pravachan image ${idx + 1}`}
                            className="max-w-full h-auto rounded-lg shadow-sm mb-4 max-h-80 object-contain mx-auto"
                          />
                        ))}
                      </div>
                    )}
                    
                    {/* Display Guruhari Ashirwad */}
                    
                      <h3 className="text-sm font-medium text-blue-700 mb-2">Guruhari Ashirwad:</h3>
                      <div className="whitespace-pre-line text-gray-700">
                        {pravachan.notes?.split('\n').map((paragraph, i) => (
                          <p key={i} className="mb-2">{paragraph}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}